import { useNavigate } from '@tanstack/react-router';
import { MenuPanel } from '@/components/ui/menu-panel';
import { ROUTES } from '@/constants/routes';
import { useGlobalSearch, MIN_SEARCH_LENGTH } from '@/hooks/useGlobalSearch';
import type { GlobalSearchHit, GlobalSearchResults as Results } from '@/apis/search';

const money = (amount?: string | null) =>
  amount === null || amount === undefined
    ? null
    : `$${Number(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

interface Section {
  key: keyof Omit<Results, 'query'>;
  title: string;
  href: (hit: GlobalSearchHit) => string | null;
}

// Customers and products have no detail page of their own yet, so their rows
// stay informational rather than linking somewhere misleading.
const SECTIONS: Section[] = [
  { key: 'orders', title: 'Orders', href: (h) => ROUTES.ORDER_DETAIL(h.id) },
  { key: 'products', title: 'Parts', href: () => null },
  { key: 'invoices', title: 'Invoices', href: (h) => `${ROUTES.INVOICE}/${h.id}` },
  { key: 'customers', title: 'Customers', href: () => null },
];

interface Props {
  query: string;
  onClose: () => void;
}

export function GlobalSearchResults({ query, onClose }: Props) {
  const navigate = useNavigate();
  const trimmed = query.trim();
  const { data, isFetching, isError } = useGlobalSearch(trimmed);

  if (trimmed.length < MIN_SEARCH_LENGTH) return null;

  const sections = SECTIONS.map((section) => ({
    ...section,
    hits: data?.[section.key] ?? [],
  })).filter((section) => section.hits.length > 0);

  const totalHits = sections.reduce((sum, s) => sum + s.hits.length, 0);

  return (
    <MenuPanel
      data-testid="global-search-results"
      // slide-in-from-top is defined in styles/animation.css. The animate-in
      // utilities used elsewhere are inert — tailwindcss-animate is not installed.
      className="absolute right-0 top-full z-50 mt-2 max-h-[70vh] w-[380px] max-w-[calc(100vw-2rem)] overflow-y-auto shadow-lg [animation:slide-in-from-top_160ms_ease-out]"
    >
      {isError ? (
        <p className="px-2 py-2 text-sm text-danger">Search failed. Try again.</p>
      ) : totalHits === 0 ? (
        <p className="px-2 py-2 text-sm text-lightGray">
          {isFetching ? 'Searching…' : `No results for "${trimmed}"`}
        </p>
      ) : (
        <>
          {sections.map((section) => (
            <div key={section.key} className="mb-1 last:mb-0">
              <p className="px-2 pt-2 pb-1 text-xs font-semibold tracking-wide text-lightGray uppercase">
                {section.title}
              </p>
              {section.hits.map((hit) => {
                const href = section.href(hit);
                const body = (
                  <>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-black">
                        {hit.label}
                      </span>
                      {hit.detail && (
                        <span className="block truncate text-xs text-lightGray">
                          {hit.detail}
                        </span>
                      )}
                    </span>
                    {money(hit.amount) && (
                      <span className="shrink-0 text-xs font-medium text-black">
                        {money(hit.amount)}
                      </span>
                    )}
                  </>
                );

                return href ? (
                  <button
                    key={`${section.key}-${hit.id}`}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onClose();
                      void navigate({ to: href });
                    }}
                    className="flex w-full cursor-pointer items-center gap-3 rounded-3xl px-2 py-2 text-left transition-colors hover:bg-primary-s50"
                  >
                    {body}
                  </button>
                ) : (
                  <div
                    key={`${section.key}-${hit.id}`}
                    className="flex w-full items-center gap-3 rounded-3xl px-2 py-2 text-left"
                  >
                    {body}
                  </div>
                );
              })}
            </div>
          ))}
          {isFetching && (
            <p className="px-2 py-1 text-xs text-lightGray">Updating…</p>
          )}
        </>
      )}
    </MenuPanel>
  );
}
